/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {memo} from 'react';
import {useSelector} from 'react-redux';
import {CRUDPermissions} from '@ion/core/src/types/entities';
import {AssetTreeRefresh} from '@ion/dls/src/editors/AssetEditor';
import {useCurrentGlobalGroup} from '../../hooks/useCurrentGlobalGroup';
import EditAssetEditor from '../admin/assets/EditAssetEditor';
import {
  editIsViewSelector as adminAssetsEditIsViewSelector,
  editShowDialogSelector as adminAssetsEditShowDialogSelector
} from '../admin/assets/selector';
import {useDeviceDetails} from './useDeviceDetails';

interface AssetDeviceDialogProps extends AssetTreeRefresh {
  includeAssetAssociation?: boolean;
  showDifferences?: boolean;
  includeDeviceGridCheckboxes?: boolean;
  includeAssetGridCheckboxes?: boolean;
  includeGroupProperties?: boolean;
  assetsPermissions?: CRUDPermissions;
  isMultiSelectDevice?: boolean;
};

/**
 * Holds dialog functionality for viewing or editing the asset associated with a device.
 */
export const AssetDeviceDialog = ({
  assetsPermissions,
  isMultiSelectDevice = false,
  includeAssetAssociation = false,
  showDifferences = false,
  includeDeviceGridCheckboxes = false,
  includeGroupProperties = true,
  includeAssetGridCheckboxes = false,
  showTreeRefresh = false
}: AssetDeviceDialogProps) => {
  const globalGroup   = useCurrentGlobalGroup();
  const showEditAsset = useSelector(adminAssetsEditShowDialogSelector);
  const showViewAsset = useSelector(adminAssetsEditIsViewSelector);

  const {
    onAssetAudit,
    onAssetEdit
  } = useDeviceDetails();

  return (
    <>
      {(showEditAsset || showViewAsset) && (
        <EditAssetEditor isCtcAdmin={showDifferences}
          assetsPermissions={assetsPermissions}
          includeAssetAssociation={includeAssetAssociation}
          includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
          includeAssetGridCheckboxes={includeAssetGridCheckboxes}
          includeGroupProperties={includeGroupProperties}
          isViewDialog={showViewAsset}
          globalGroup={globalGroup}
          onEdit={onAssetEdit}
          onAudit={onAssetAudit}
          showTreeRefresh={showTreeRefresh}
          isMultiSelectDevice={isMultiSelectDevice} />
      )}
    </>
  );
};

export default memo(AssetDeviceDialog);
